<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useProfilesStore } from '@/stores/riskProfileStore';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Card from 'primevue/card';
import Tag from 'primevue/tag';
import Toast from 'primevue/toast';
import Skeleton from 'primevue/skeleton';
import InputText from 'primevue/inputtext';
import vTooltip from 'primevue/tooltip';
import type { ProfileGroup } from '@/types/risk-profile';
import { getEntityTypeIcon } from '@/utils/kyc';
import { formatDate } from '@/utils/formatters';
import BackButton from '@/components/base/BackButton.vue';
import { useRiskProfileOptions } from '@/composables/useRiskProfileOptions';
import { usePolicyDecisionDisplay } from '@/composables/usePolicyDecisionDisplay';
import KycPricingTableBadge from '@/components/accountAdmin/configuration/risk/KycPricingTableBadge.vue';
import ProfileDeleteConfirmDialog from '@/components/accountAdmin/configuration/risk/ProfileDeleteConfirmDialog.vue';
import AppPagination from '@/components/common/AppPagination.vue';
import { MODULE_ICONS } from '@/constants/icons';
import PolicyListFilters from '@/components/common/PolicyListFilters.vue';
import type { PolicyVisibilityFilter, PolicyProductionFilter } from '@/types/versioning';
import { productionFilterToIsActive } from '@/utils/versioning';

// --- Interfaces ---

interface RowClickEvent {
  data: ProfileGroup;
}

// --- Component Setup ---
const router = useRouter();
const store = useProfilesStore();
const visibilityFilter = ref<PolicyVisibilityFilter>('active');
const productionFilter = ref<PolicyProductionFilter>('all');
useRiskProfileOptions();

// --- Server-side list controls (pagination / sort / search / status filter) ---
// Maps the sortable column `field`s to the backend sort keys the API understands.
const SORT_FIELD_MAP: Record<string, string> = {
    profileName: 'name',
    profileType: 'entity_type',
    createdAt: 'created_at',
    last_updated_at: 'updated_at',
};

const searchText = ref(store.searchQuery);
// Search fires on blur / Enter (see the input's @blur + @keyup.enter), not on every keystroke.
const applySearch = () => store.setSearch(searchText.value);

watch(visibilityFilter, (val) => store.setStatusFilter(val));
watch(productionFilter, (val) => store.setIsActiveFilter(productionFilterToIsActive(val)));

const rowClass = (data: ProfileGroup) => (data.deletedAt ? 'kyx-policy-row--deleted' : '');

const currentPage = computed(() => Math.floor(store.first / store.pageSize) + 1);

const onPageChange = (page: number) => {
    store.setPage((page - 1) * store.pageSize, store.pageSize);
};

const onRowsChange = (rows: number) => {
    // Reset to the first page when the page size changes.
    store.setPage(0, rows);
};

const onSort = (event: { sortField?: any; sortOrder?: number | null }) => {
    const field = typeof event.sortField === 'string' ? event.sortField : '';
    const backendField = field ? SORT_FIELD_MAP[field] ?? null : null;
    store.setSort(backendField, event.sortOrder === 1 ? 'asc' : 'desc');
};

const { hasPolicyDecision, policyDecisionTooltip } = usePolicyDecisionDisplay(store);

const emptyStateTitle = computed(() => {
    if (store.searchQuery.trim()) {
        return 'Nenhuma Política Encontrada';
    }

    if (visibilityFilter.value === 'active') {
        return 'Nenhuma Política Ativa';
    }

    if (visibilityFilter.value === 'deleted') {
        return 'Nenhuma Política Excluída';
    }

    return 'Nenhuma Política Encontrada';
});

const emptyStateDescription = computed(() => {
    if (store.searchQuery.trim()) {
        return 'Tente ajustar o nome buscado ou altere o filtro de status.';
    }

    if (visibilityFilter.value === 'active') {
        return 'Não há políticas ativas para exibir no momento.';
    }

    if (visibilityFilter.value === 'deleted') {
        return 'Não há políticas excluídas para exibir no momento.';
    }

    return 'Comece criando a sua primeira política.';
});

onMounted(() => {
    store.fetchData();
});

const handleAddProfile = () => {
    router.push({ name: 'account-admin-profile-wizard' });
};

const handleOpenReportsDashboard = (event: Event, group: ProfileGroup) => {
    event.stopPropagation();
    router.push({
        name: 'global-kyc-policy-reports',
        params: { riskProfileId: group.profileGroupId },
        query: {
            name: group.profileName,
            entityType: group.entity_type,
        },
    });
};

const onRowClick = (event: RowClickEvent) => {
    if (event.data && !event.data.deletedAt) {
        router.push({ name: 'account-admin-profile-details', params: { id: event.data.profileGroupId } });
    }
};

const profileDeleteDialog = ref<InstanceType<typeof ProfileDeleteConfirmDialog> | null>(null);

const confirmDelete = (event: Event, group: ProfileGroup) => {
    event.stopPropagation();
    profileDeleteDialog.value?.open(group);
};
</script>

<template>
  <!-- -m-6 cancels the app layout's p-6 (white) so the gray page background reaches the edges; p-6 pads the content inside it. -->
  <div class="-m-6 p-6 bg-gray-50 min-h-screen">
    <Toast />
    <ProfileDeleteConfirmDialog ref="profileDeleteDialog" />
    <div class="max-w-7xl mx-auto">
      <header class="mb-6">
        <div class="flex flex-col sm:flex-row justify-between sm:items-center">
          <div class="flex items-center">
            <BackButton to="/settings" />
            <div>
              <h1 class="text-4xl font-bold text-gray-800 flex items-center gap-3">
                  <BaseIcon :size="36" class="text-primary-600">{{ MODULE_ICONS.kyx }}</BaseIcon>
                  Políticas KYX
              </h1>
              <p class="text-lg text-gray-500 mt-2">Crie e gerencie as políticas de risco para sua organização.</p>
            </div>
          </div>
          <Button label="Adicionar Nova Política" class="mt-4 sm:mt-0" @click="handleAddProfile">
            <template #icon><span class="material-icons mr-2">add</span></template>
          </Button>
        </div>
      </header>
      <Card>
        <template #content>
          <DataTable
            :value="store.profileGroups"
            :loading="store.isLoading"
            lazy
            :rows="store.pageSize"
            dataKey="profileGroupId"
            :rowClass="rowClass"
            @row-click="onRowClick"
            @sort="onSort"
            class="p-datatable-clickable-rows"
          >
            <template #header>
              <div class="flex justify-between items-center flex-wrap gap-4">
                <div class="text-xl font-semibold text-gray-700">Todas as Políticas</div>
                <div class="flex items-center gap-4">
                  <PolicyListFilters
                    v-model:visibility="visibilityFilter"
                    v-model:production="productionFilter"
                  />
                  <span class="relative">
                    <span class="material-icons absolute top-1/2 -mt-3 left-3 text-surface-400">search</span>
                    <InputText v-model="searchText" placeholder="Buscar por nome" class="pl-10 font-normal" @blur="applySearch" @keyup.enter="applySearch"/>
                  </span>
                </div>
              </div>
            </template>
            <template #empty>
              <div class="text-center p-8">
                <span class="material-icons text-6xl text-gray-300 mb-2">folder_open</span>
                <h3 class="text-xl font-semibold text-gray-700">{{ emptyStateTitle }}</h3>
                <p class="text-gray-500 mb-4">{{ emptyStateDescription }}</p>
                <Button label="Adicionar Nova Política" @click="handleAddProfile">
                  <template #icon><span class="material-icons mr-2">add</span></template>
                </Button>
              </div>
            </template>
            <template #loading>
              <div class="p-4"><Skeleton v-for="i in 5" :key="i" height="3.5rem" class="mb-2"></Skeleton>
              </div>
            </template>

            <Column field="profileName" header="Nome da Política" sortable>
              <template #body="{ data }: { data: ProfileGroup }">
                <div class="flex items-center gap-2">
                  <span class="kyx-policy-name font-semibold text-gray-800 group-hover:underline">{{ data.profileName }}</span>
                </div>
              </template>
            </Column>

            <Column field="profileType" header="Tipo de Política" style="width: 12rem; text-align: center;" sortable>
              <template #body="{ data }: { data: ProfileGroup }">
                <BaseIcon class="text-[18px] text-gray-700">{{
                  getEntityTypeIcon(data.entity_type)
                }}
                </BaseIcon>
              </template>
            </Column>

            <Column header="Em Produção" style="width: 10rem; text-align: center;">
                <template #body="{ data }: { data: ProfileGroup }">
                    <span v-if="data.isActive" class="material-icons text-green-500" v-tooltip.top="'Esta política tem uma versão em produção'">check_circle</span>
                    <span v-else class="material-icons text-gray-400" v-tooltip.top="'Nenhuma versão em produção'">do_not_disturb_on</span>
                </template>
            </Column>
              <Column header="Possui Rascunho" style="width: 8rem; text-align: center;">
                <template #body="{ data }: { data: ProfileGroup }">
                    <span v-if="store.getDraft(data)" class="material-icons text-yellow-500" v-tooltip.top="'Um rascunho está em andamento'">edit</span>
                </template>
            </Column>

            <Column header="Versão" field="currentInstanceId" style="width: 10rem">
              <template #body="{ data }: { data: ProfileGroup }">
                <Tag v-if="store.getCurrentInstance(data)" :value="`${store.getCurrentInstance(data)?.version_number}`" severity="success" />
              </template>
            </Column>

            <Column header="Decisão Automática" style="width: 12rem; text-align: center;">
              <template #body="{ data }: { data: ProfileGroup }">
                <span v-if="hasPolicyDecision(data)" class="material-icons text-green-500" v-tooltip.top="policyDecisionTooltip(data)">gavel</span>
                <span v-else class="material-icons text-gray-400" v-tooltip.top="policyDecisionTooltip(data)">do_not_disturb_on</span>
              </template>
            </Column>

            <Column header="Tokens" style="width: 14rem">
                <template #body="{ data }: { data: ProfileGroup }">
                    <KycPricingTableBadge :group="data" :store="store" />
                </template>
            </Column>

            <Column header="Criado em" sortable field="createdAt" style="width: 10rem">
              <template #body="{ data }: { data: ProfileGroup }">{{ formatDate(data.createdAt) }}</template>
            </Column>

            <Column header="Versão atual por" style="width: 10rem">
              <template #body="{ data }: { data: ProfileGroup }">{{ data.currentInstanceAuthorName }}</template>
            </Column>

            <Column header="Última Atualização" sortable field="last_updated_at" style="width: 10rem">
              <template #body="{ data }: { data: ProfileGroup }">
                {{ formatDate(data.last_updated_at) }}
              </template>
            </Column>

            <Column style="width: 8rem; text-align: right;">
                <template #body="{ data }: { data: ProfileGroup }">
                    <div class="flex items-center justify-end gap-2">
                        <Button
                            text
                            rounded
                            v-tooltip.top="'Ver relatórios KYX desta política'"
                            @click="handleOpenReportsDashboard($event, data)"
                        >
                          <template #icon>
                            <BaseIcon>analytics</BaseIcon>
                          </template>
                        </Button>
                        <Button
                            v-if="!data.isActive && !data.deletedAt"
                            severity="danger"
                            text
                            rounded
                            v-tooltip.top="'Excluir Política'"
                            @click="confirmDelete($event, data)"
                        >
                          <template #icon>
                            <BaseIcon>delete</BaseIcon>
                          </template>
                        </Button>
                        <Tag v-if="data.deletedAt" value="Excluída" severity="danger" />
                        <span v-else class="material-icons text-gray-300 ml-1 group-hover:text-blue-500 transition-colors">chevron_right</span>
                    </div>
                </template>
            </Column>
          </DataTable>
          <AppPagination
            v-if="store.totalRecords > 0"
            :totalRecords="store.totalRecords"
            :rows="store.pageSize"
            :page="currentPage"
            :loading="store.isLoading"
            @page-change="onPageChange"
            @rows-change="onRowsChange"
          />
        </template>
      </Card>
    </div>
  </div>
</template>

<style scoped>
.p-datatable-clickable-rows .p-datatable-tbody > tr { cursor: pointer; transition: background-color 0.2s; }
.p-datatable-clickable-rows .p-datatable-tbody > tr:not(.p-datatable-emptymessage):hover { background-color: var(--surface-50) !important; }

/* Deleted rows: red-tinted, name struck through (same as the Pulse and KYT hubs). */
:deep(tr.kyx-policy-row--deleted) { background-color: #fef2f2; }
:deep(tr.kyx-policy-row--deleted:hover) { background-color: #fee2e2 !important; }
:deep(tr.kyx-policy-row--deleted .kyx-policy-name) { text-decoration: line-through; text-decoration-color: #ef4444; color: #9ca3af; }
</style>
